/**
 * /genies — the immersive "all genies" gallery (configurator direction).
 *
 * The front door of the Genie view: ultra-clean, full-bleed, ONLY the genie
 * cards. No nav, no search, no filters. The lightest possible header ("Your
 * genies" + a count) orients, then gets out of the way; an image-forward 3-up
 * grid of GalleryGenieCards carries the page. Each card -> /genies/:id (viewer).
 *
 * Data: the REAL useAgents(250) infinite hook, now group-filtered, plus the REAL
 * useAgentGroups / useAgentGroupCounts hooks for the group tabs. Four states:
 *  - Loading: a 6-card skeleton grid matching the layout.
 *  - Empty: a purpose-built "No genies yet" with a create CTA (honest — only when
 *    the account genuinely has no genies; a filter with no matches shows its own
 *    quiet empty line, never fabricated cards).
 *  - Error: an inline error card (no toast for a page load).
 *  - Success: the rendered grid (+ a quiet "Load more" when more pages exist).
 *
 * Brand law: genie tokens only; signature gradient for the hero create CTA;
 * *Voice* never shown here (gallery has no heading needing it). Lucide inline only.
 */
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Loader2, Plus } from "lucide-react";
import { useAgents } from "@/hooks/agent/useAgents";
import { useAgentGroups, useAgentGroupCounts } from "@/hooks/agent";
import { Button } from "@/components/ui/button";
import { GalleryGenieCard } from "./GalleryGenieCard";
import { GalleryCreateCard } from "./GalleryCreateCard";
import { GalleryGroupTabs } from "./GalleryGroupTabs";
import { GallerySkeleton } from "./GallerySkeleton";
import { GenieIcon } from "@/components/ui/genie-icons/GenieIcon";

const GalleryPage = () => {
  const navigate = useNavigate();
  const [selectedGroupId, setSelectedGroupId] = useState<string | null>(null);

  const { data: groups } = useAgentGroups();
  const { data: serverGroupCounts } = useAgentGroupCounts();

  const {
    data,
    isLoading,
    isError,
    error,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    refetch,
  } = useAgents(250, selectedGroupId ? { groupId: selectedGroupId } : undefined);

  const genies = data?.allAgents ?? [];

  // Unfiltered total drives the header + the "All" pill so they stay honest
  // while a group filter narrows the grid. Prefer the real per-group counts
  // (their sum is the account total); fall back to the unfiltered page total.
  const countsTotal = serverGroupCounts
    ? Object.values(serverGroupCounts).reduce((sum, n) => sum + n, 0)
    : undefined;
  const total =
    countsTotal && countsTotal > 0
      ? countsTotal
      : selectedGroupId === null
        ? (data?.total ?? genies.length)
        : genies.length;

  const isFiltering = selectedGroupId !== null;

  return (
    <div className="flex flex-col h-screen w-full bg-[#F9FAFC] overflow-hidden">
      <div className="flex-1 min-h-0 overflow-auto">
        <div className="flex flex-col items-center px-6 md:px-10 pt-14 pb-14">
          <div className="w-full max-w-[1040px]">
            {/* the lightest possible header — orient, then get out of the way */}
            <div
              data-hg-block="header"
              className="mb-[34px] flex items-baseline gap-3"
            >
              <h1 className="text-[28px] font-extrabold tracking-[-0.6px] text-[#222B45]">
                Your genies
              </h1>
              {!isLoading && !isError && total > 0 && (
                <span className="text-[15px] font-semibold text-gray-400">
                  {total}
                </span>
              )}
            </div>

            {/* immersive grid — nothing but the genies */}
            {isLoading ? (
              <GallerySkeleton />
            ) : isError ? (
              <div className="rounded-[22px] border border-genie-border bg-white px-8 py-12 text-center shadow-sm">
                <p className="text-[17px] font-bold text-[#222B45]">
                  We could not load your genies
                </p>
                <p className="mt-1.5 text-[13.5px] text-gray-500">
                  {error instanceof Error
                    ? error.message
                    : "Something went wrong. Please try again."}
                </p>
                <Button
                  type="button"
                  onClick={() => refetch()}
                  className="mt-5 h-10 rounded-full bg-gradient-to-r from-[#4E9CFF] to-[#FF4DB8] px-6 text-[13.5px] font-bold text-white shadow-lg shadow-genie-primary/30 hover:opacity-90"
                >
                  Try again
                </Button>
              </div>
            ) : genies.length === 0 && !isFiltering ? (
              <div className="rounded-[22px] border border-genie-border bg-white px-8 py-16 text-center shadow-sm">
                <div className="mx-auto flex h-[88px] w-[88px] items-center justify-center rounded-full bg-genie-primary/10">
                  <GenieIcon
                    name="genie-lamp"
                    size={56}
                    className="h-[56px] w-[56px]"
                  />
                </div>
                <p className="mt-5 text-[19px] font-extrabold tracking-[-0.3px] text-[#222B45]">
                  No genies yet
                </p>
                <p className="mx-auto mt-1.5 max-w-[380px] text-[13.5px] text-gray-500">
                  Summon your first genie and it will start answering for your
                  business in minutes.
                </p>
                <Button
                  type="button"
                  onClick={() => navigate("/welcome")}
                  className="mt-6 h-11 rounded-full bg-gradient-to-r from-[#4E9CFF] to-[#FF4DB8] px-7 text-[14px] font-bold text-white shadow-lg shadow-genie-primary/30 hover:opacity-90"
                >
                  <Plus className="mr-1.5 h-4 w-4" />
                  Create your first genie
                </Button>
              </div>
            ) : (
              <>
                {/* REAL group tabs — All + per-group + Ungrouped, with real counts */}
                <GalleryGroupTabs
                  groups={groups ?? []}
                  agents={genies}
                  selectedGroupId={selectedGroupId}
                  onGroupChange={setSelectedGroupId}
                  totalCount={total}
                  serverGroupCounts={serverGroupCounts}
                />

                {genies.length === 0 ? (
                  // Honest filtered-empty: a real group with no genies yet.
                  <div className="rounded-[22px] border border-genie-border bg-white px-8 py-14 text-center shadow-sm">
                    <p className="text-[16px] font-bold text-[#222B45]">
                      No genies in this group yet
                    </p>
                    <p className="mx-auto mt-1.5 max-w-[360px] text-[13px] text-gray-500">
                      Move a genie into this group, or pick another group above.
                    </p>
                    <button
                      type="button"
                      onClick={() => setSelectedGroupId(null)}
                      className="mt-5 inline-flex items-center rounded-full border border-genie-border bg-white px-5 py-2 text-[13px] font-semibold text-gray-500 transition-colors hover:text-genie-primary"
                    >
                      View all genies
                    </button>
                  </div>
                ) : (
                  <div
                    data-hg-block="genies-grid"
                    className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-[26px]"
                  >
                    {/* always-first create tile -> immersive onboarding wizard */}
                    {!isFiltering && <GalleryCreateCard />}
                    {genies.map((genie, i) => (
                      <GalleryGenieCard key={genie.id} genie={genie} index={i} />
                    ))}
                  </div>
                )}

                {hasNextPage && genies.length > 0 && (
                  <div className="mt-9 flex justify-center">
                    <button
                      type="button"
                      onClick={() => fetchNextPage()}
                      disabled={isFetchingNextPage}
                      className="inline-flex items-center gap-2 rounded-full border border-genie-border bg-white px-6 py-2.5 text-[13px] font-semibold text-gray-500 shadow-sm transition-colors hover:text-genie-primary disabled:opacity-60"
                    >
                      {isFetchingNextPage && (
                        <Loader2 className="h-3.5 w-3.5 animate-spin" />
                      )}
                      {isFetchingNextPage ? "Loading" : "Load more genies"}
                    </button>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default GalleryPage;
